import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, ModalController, ToastController } from '@ionic/angular';
import { ReportsService } from '../reports/reports.service';
import { BaseClass } from '../shared/classes/base';
import { CyclePreview } from '../shared/interfaces/cycle.interface';
import { SelectCycleComponent } from '../shared/modals/select-cycle/select-cycle.component';
import { AuthService } from '../shared/services/auth.service';
import { CycleService } from '../shared/services/cycle.service';
import { TranslationService } from '../shared/services/translation.service';
import { PerformanceCycle } from './_interfaces/performance-cycle.interface';
import { CycleTypeSelectorComponent } from './configuration/cycle-type-selector/cycle-type-selector.component';
import { PerformanceCycleService } from './performance-cycles.service';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Component({
    selector: 'app-performance-cycles',
    templateUrl: './performance-cycles.page.html',
    styles: ['.year-select { width:120px; }'],
    styleUrls: ['./performance-cycles.page.scss'],
})
export class PerformanceCyclesPage extends BaseClass {

    public performanceCycles: Array<CyclePreview>;
    public cycles: string = 'openCycles';
    public isAdmin: boolean = false;
    public cycleConfigurationAvailable = false;
    public isLoading: boolean = false;
    public isLoadingSteps: boolean = false;
    public cyclesToSelect: PerformanceCycle[] = [];
    public selectedCycle: PerformanceCycle;
    public selectedCycleId: number;
    public selectedYear: number;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _loadingController: LoadingController,
        private _performanceCycleService: PerformanceCycleService,
        private _authService: AuthService,
        private _reportsService: ReportsService,
        private _router: Router,
        private _cycleService: CycleService,
        private _configService: ConfigService,
        private _modalController: ModalController,
    ) {
        super(_toastController, _translationService);
    }

    async ionViewWillEnter() {
        await this._loadCycles();

        const roles = this._authService.getUserRoles();
        this.isAdmin = roles.includes('Administrator');
        this.cycleConfigurationAvailable = roles.includes('Administrator') || roles.includes('HR');
    }

    public async goToAddCycleConfig() {
        const showPartnerCycleConfiguration = this._configService.settings.features.showPartnerCycleConfiguration;
        if (!showPartnerCycleConfiguration) {
            this._router.navigate(['performance-cycles/0']);
            return;
        }

        const modal = await this._modalController.create({
            component: CycleTypeSelectorComponent,
            cssClass: 'small-modal',
        });

        modal.onDidDismiss().then(() => {
        })

        await modal.present();
    }

    public goToEditCycleConfig(): void {
        if (this.selectedCycleId)
            this._router.navigate(['performance-cycles/' + this.selectedCycleId]);
    }

    public goBack(): void {
        this._router.navigate(['dashboard']);
    }

    private async _loadCycles(): Promise<void> {
        const loading = await this._loadingController.create();

        this._reportsService.getUserCycles().subscribe(async (response) => {
            await loading.present();
            const responseData = response.data as any[];

            this.cyclesToSelect = responseData.map(
                (d) =>
                    new PerformanceCycle(
                        d.cycleId,
                        d.name,
                        d.year,
                        d.term,
                        d.defaultCycle
                    )
            );

            if (!this._cycleService.getCycle()) {
                this.selectedCycle = this.cyclesToSelect.filter(c => c.defaultCycle === true)[0];
                if (this.selectedCycle) this._cycleService.setCycle(this.selectedCycle.id);
            } else
                this.selectedCycle = this.cyclesToSelect.filter(c => c.id === parseInt(this._cycleService.getCycle()))[0];

            this.selectedCycleId = this.selectedCycle ? this.selectedCycle.id : undefined;
            this.selectedYear = this.selectedCycle ? this.selectedCycle.year : undefined;
            this.isLoading = true;

            this.loadYearCycles(loading);
        }, (error) => {
            loading.dismiss();
            this.isLoading = false;
            this.getErrorNotification(error);
        });
    }

    private loadYearCycles(loading: HTMLIonLoadingElement) {
        this._performanceCycleService.getPeformanceCycles(this.selectedYear, this.selectedCycleId)
            .subscribe((response) => {
                this.performanceCycles = response.data;
                if (response.data.length) this._cycleService.setCycle(response.data[0].id);
                loading.dismiss();
                this.isLoading = false;
                this.isLoadingSteps = true;
                this._performanceCycleService.getPerformanceStepsFromCycleByYear(this.selectedYear, this.selectedCycleId)
                    .subscribe((response) => {
                        this.performanceCycles.map(c =>
                            c.processes.forEach(p => {
                                const process = response.data.find(s => s.id === p.id)
                                p.finishedAmount = process.finishedAmount;
                                p.unfinishedAmount = process.unfinishedAmount;
                            })
                        );
                        this.isLoadingSteps = false;
                    }, (error) => {
                        this.isLoadingSteps = false;
                        this.getErrorNotification(error);
                    });
            }, (error) => {
                loading.dismiss();
                this.isLoading = false;
                this.getErrorNotification(error);
            });
    }

    public async selectYear(cycleId) {
        if (this.isLoading) return;
        this.selectedCycle = this.cyclesToSelect.find(c => c.id === cycleId);
        this.selectedCycleId = this.selectedCycle ? this.selectedCycle.id : undefined;
        this.selectedYear = this.selectedCycle ? this.selectedCycle.year : undefined;
        const loading = await this._loadingController.create();
        await loading.present();
        this.loadYearCycles(loading);
    }

    public async selectCycle() {
        const modal = await this._modalController.create({
            component: SelectCycleComponent,
            cssClass: 'select-cycle-modal',
            componentProps: {
                cycles: this.cyclesToSelect,
                selectedCycleId: this.selectedCycleId
            }
        });

        modal.onDidDismiss().then((response) => {
            if (response.data) {
                this.selectedCycleId = response.data;
                this.selectedCycle = this.cyclesToSelect.find(c => c.id === response.data);
                this.selectYear(this.selectedCycleId);
            }
        })

        await modal.present();
    }
}
